import { useMemo } from "react";

const TYPE = {
    STD: "CDESTANDARD",
    GLE: "GLE",
    EO: "EVIDENCE_OUTCOME",
    EOC: "EVIDENCE_OUTCOME_CHILD"
};

// Determines whether a node should appear in the hierarchy.
// TEMPLATE mode: requires both included and reportFlag (district + school selected it).
// CONTENT mode: requires only reportFlag (district selected it).
const isLeafReportable = (node, contentType, inputType) => {
    const isRatableType = contentType === TYPE.GLE || contentType === TYPE.EO || contentType === TYPE.EOC;
    if (inputType === "TEMPLATE") {
        return isRatableType && node?.included && node?.reportFlag;
    }
    return isRatableType && node?.reportFlag;
};

export function useReportableHierarchy(selectedTemplate, inputType = "TEMPLATE") {
    return useMemo(() => {
        const result = { tree: [], flat: [], counts: { standards: 0, gles: 0, eos: 0, eocs: 0 } };
        const standards = selectedTemplate?.cdeStandards || [];

        const makeId = (contentType, key) => `${contentType}:${String(key)}`;
        const pushFlat = (id, contentType, label, breadcrumb) =>
            result.flat.push({ id, contentType, label, breadcrumb });

        const processEOC = (eoc, std, gle, eo) => {
            const keep = isLeafReportable(eoc, TYPE.EOC, inputType);
            if (!keep) return null;
            const node = {
                id: makeId(TYPE.EOC, eoc.key),
                contentId: Number(eoc.key),
                gradeToContentId: eoc.gradeToContentKey,
                contentType: TYPE.EOC,
                label: eoc.body,
                included: eoc.included,
                reportFlag: eoc.reportFlag,
                templateContentId: eoc.templateContentId,
                children: []
            };
            result.counts.eocs++;
            pushFlat(
                node.id,
                node.contentType,
                node.label,
                `${std.standardName} › ${gle.gleShortName} › ${truncate(eo.body, 80)} › ${truncate(eoc.body, 80)}`
            );
            return node;
        };

        const processEO = (eo, std, gle) => {
            const kids = (eo.evidenceOutcomeChildList || [])
                .map((eoc) => processEOC(eoc, std, gle, eo))
                .filter(Boolean);

            // EO is kept if it’s reportable OR has at least one kept child
            const keep = isLeafReportable(eo, TYPE.EO, inputType) || kids.length > 0;
            if (!keep) return null;

            const node = {
                id: makeId(TYPE.EO, eo.key),
                contentType: TYPE.EO,
                contentId: Number(eo.key),
                gradeToContentId: eo.gradeToContentKey,
                label: eo.body,
                included: eo.included,
                reportFlag: eo.reportFlag,
                templateContentId: eo.templateContentId,
                children: kids
            };
            result.counts.eos++;
            pushFlat(
                node.id,
                node.contentType,
                node.label,
                `${std.standardName} › ${gle.gleShortName} › ${truncate(eo.body, 120)}`
            );
            return node;
        };

        const processGLE = (gle, std) => {
            const eos = (gle.evidenceOutcomes || []).map((eo) => processEO(eo, std, gle)).filter(Boolean);
            // Keep GLE if it is itself reportable (GLE-level subject) OR has reportable EO descendants
            const gleIsReportable = isLeafReportable(gle, TYPE.GLE, inputType);
            if (!gleIsReportable && eos.length === 0) return null;

            const node = {
                id: makeId(TYPE.GLE, gle.key),
                contentType: TYPE.GLE,
                contentId: Number(gle.key),
                gradeToContentId: gle.gradeToContentKey,
                label: gle.gleName,
                title: gle.gleShortName,
                included: gle.included,
                reportFlag: gle.reportFlag,
                templateContentId: gle.templateContentId,
                children: eos
            };
            result.counts.gles++;
            pushFlat(node.id, node.contentType, node.label, `${std.standardName} › ${gle.gleShortName}`);
            return node;
        };

        const processSTD = (std) => {
            const gles = (std.gles || []).map((gle) => processGLE(gle, std)).filter(Boolean);
            if (gles.length === 0) return null; // keep only if it has reportable descendants

            const node = {
                id: makeId(TYPE.STD, std.key),
                contentType: TYPE.STD,
                contentId: Number(std.key),
                gradeToContentId: std.gradeToContentKey,
                label: std.standardName,
                included: std.included,
                reportFlag: std.reportFlag,
                templateContentId: std.templateContentId,
                children: gles
            };
            result.counts.standards++;
            pushFlat(node.id, node.contentType, node.label, std.standardName);
            return node;
        };

        for (const std of standards) {
            const stdNode = processSTD(std);
            if (stdNode) result.tree.push(stdNode);
        }

        return result;
    }, [inputType, selectedTemplate]);
}

function truncate(str, n) {
    if (!str) return "";
    return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}
